import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { COLORS, GRADIENTS, SHADOWS } from './ui/crm-ui';

interface NotificationMeta {
  fileName?: string;
  folderName?: string | null;
  promptName?: string;
  sharePointUrl?: string | null;
}

interface Notification {
  id: number;
  type: string;
  title: string;
  message: string | null;
  is_read: boolean;
  metadata: string | null;
  created_at: string;
}

function BellIcon({ hasUnread }: { hasUnread: boolean }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke={hasUnread ? COLORS.purple : COLORS.mid}
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    >
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

function ExcelIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke={COLORS.green} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  );
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const POLL_INTERVAL_MS = 10_000;

interface Props {
  baseApiUrl: string;
}

export default function NotificationBell({ baseApiUrl }: Props) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [open, setOpen] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const fetchNotifications = useCallback(async () => {
    if (!baseApiUrl) return;
    try {
      const { data } = await axios.get<Notification[]>(`${baseApiUrl}/api/notifications`);
      setNotifications(data);
    } catch {
      // Silently ignore — bell is non-critical
    }
  }, [baseApiUrl]);

  // Initial fetch + 30-second poll
  useEffect(() => {
    fetchNotifications();
    const id = setInterval(fetchNotifications, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  async function markOneRead(n: Notification) {
    if (!n.is_read) {
      setNotifications((prev) => prev.map((x) => x.id === n.id ? { ...x, is_read: true } : x));
      axios.patch(`${baseApiUrl}/api/notifications/${n.id}/read`).catch(() => {});
    }

    // Navigate to Documents view, filtered to the folder where the file was saved
    setOpen(false);
    const meta: NotificationMeta = n.metadata ? JSON.parse(n.metadata) : {};
    if (meta.folderName) {
      navigate(`/documents?folder=${encodeURIComponent(meta.folderName)}`);
    } else {
      navigate('/documents');
    }
  }

  async function markAllRead() {
    setMarkingAll(true);
    try {
      await axios.patch(`${baseApiUrl}/api/notifications/read-all`);
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    } catch {
      // ignore
    } finally {
      setMarkingAll(false);
    }
  }

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      {/* Bell button */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Notifications"
        style={{
          position: 'relative',
          width: 36, height: 36, borderRadius: 9,
          border: `1px solid ${open ? COLORS.purple + '40' : COLORS.border}`,
          background: open ? COLORS.purpleSoft : COLORS.white,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', transition: 'all 0.15s',
          boxShadow: open ? `0 0 0 3px rgba(124,58,237,0.10)` : SHADOWS.sm,
        }}
      >
        <BellIcon hasUnread={unreadCount > 0} />
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute', top: -4, right: -4,
            minWidth: 16, height: 16, borderRadius: 999,
            background: GRADIENTS.ai,
            color: '#fff', fontSize: 10, fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 4px',
            border: `2px solid ${COLORS.white}`,
            boxShadow: '0 2px 6px rgba(18,38,170,0.25)',
          }}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div
          className="crm-anim-slideDown"
          style={{
            position: 'absolute', top: 'calc(100% + 8px)', right: 0,
            width: 340, maxHeight: 440,
            background: COLORS.white,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 14,
            boxShadow: '0 16px 40px rgba(18,38,170,0.12), 0 2px 8px rgba(0,0,0,0.06)',
            zIndex: 250,
            display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Dropdown header */}
          <div style={{
            padding: '12px 16px 10px',
            borderBottom: `1px solid ${COLORS.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexShrink: 0,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <BellIcon hasUnread={unreadCount > 0} />
              <span style={{ fontWeight: 700, fontSize: 14, color: COLORS.dark }}>Notifications</span>
              {unreadCount > 0 && (
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 999,
                  background: COLORS.purpleSoft, color: COLORS.purple,
                }}>
                  {unreadCount} new
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={markAllRead}
                disabled={markingAll}
                style={{
                  fontSize: 11, fontWeight: 600, color: COLORS.purple,
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontFamily: 'inherit', opacity: markingAll ? 0.5 : 1,
                }}
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Notification list */}
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {notifications.length === 0 ? (
              <div style={{
                padding: '32px 16px', textAlign: 'center',
                color: COLORS.mid, fontSize: 13,
              }}>
                No notifications yet.
              </div>
            ) : (
              notifications.map((n) => {
                const meta: NotificationMeta = n.metadata ? JSON.parse(n.metadata) : {};
                return (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => markOneRead(n)}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'flex-start', gap: 12,
                      padding: '12px 16px',
                      borderBottom: `1px solid ${COLORS.border}`,
                      background: n.is_read ? 'transparent' : COLORS.purpleSoft,
                      border: 'none',
                      cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                      transition: 'background 0.12s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = COLORS.pageBg; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = n.is_read ? 'transparent' : COLORS.purpleSoft; }}
                  >
                    {/* Icon */}
                    <div style={{
                      width: 34, height: 34, borderRadius: 9, flexShrink: 0,
                      background: COLORS.greenSoft,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      border: `1px solid rgba(22,163,74,0.15)`,
                    }}>
                      <ExcelIcon />
                    </div>

                    {/* Content */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 13, fontWeight: n.is_read ? 500 : 700,
                        color: COLORS.dark, lineHeight: 1.3,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {n.title}
                      </div>
                      {meta.fileName && (
                        <div style={{
                          fontSize: 11, color: COLORS.mid, marginTop: 2,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {meta.fileName}
                        </div>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                        <span style={{ fontSize: 11, color: COLORS.mid }}>{timeAgo(n.created_at)}</span>
                        {meta.folderName && (
                          <>
                            <span style={{ color: COLORS.border, fontSize: 11 }}>·</span>
                            <span style={{
                              fontSize: 11, color: COLORS.purple, fontWeight: 600,
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              maxWidth: 140,
                            }}>
                              {meta.folderName}
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Unread dot */}
                    {!n.is_read && (
                      <div style={{
                        width: 8, height: 8, borderRadius: '50%',
                        background: GRADIENTS.ai, flexShrink: 0, marginTop: 4,
                      }} />
                    )}
                  </button>
                );
              })
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div style={{
              padding: '8px 16px',
              borderTop: `1px solid ${COLORS.border}`,
              flexShrink: 0,
              fontSize: 11, color: COLORS.mid, textAlign: 'center',
            }}>
              Showing last {notifications.length} notification{notifications.length !== 1 ? 's' : ''}
              {' · '}Click a notification to open the folder
            </div>
          )}
        </div>
      )}
    </div>
  );
}
